/**
 * Razorpay Payment Service
 * Handles Razorpay checkout integration
 */

declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  name: string;
  description: string;
  handler: (response: RazorpayPaymentResponse) => void;
  prefill?: {
    contact?: string;
    email?: string;
  };
  theme?: {
    color: string;
  };
  modal?: {
    ondismiss: () => void;
  };
}

export interface RazorpayPaymentResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

/**
 * Load Razorpay script dynamically
 */
export function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay script'));
    document.body.appendChild(script);
  });
}

/**
 * Initialize Razorpay checkout
 */
export async function initializeRazorpayCheckout(
  orderDetails: {
    orderId: string;
    amount: number;
    currency: string;
    keyId: string;
  },
  callbacks: {
    onSuccess: (response: RazorpayPaymentResponse) => void;
    onError: (error: string) => void;
    onDismiss?: () => void;
  },
  options?: {
    name?: string;
    description?: string;
    contact?: string;
    email?: string;
  }
): Promise<void> {
  try {
    // Load Razorpay script if not already loaded
    await loadRazorpayScript();

    if (!window.Razorpay) {
      throw new Error('Razorpay SDK not loaded');
    }

    const razorpayOptions: RazorpayOptions = {
      key: orderDetails.keyId,
      amount: orderDetails.amount * 100, // Convert to paise
      currency: orderDetails.currency,
      order_id: orderDetails.orderId,
      name: options?.name || 'Partner Registration',
      description: options?.description || 'Partner Registration Payment',
      prefill: {
        contact: options?.contact,
        email: options?.email,
      },
      theme: {
        color: '#2563eb',
      },
      handler: (response) => {
        callbacks.onSuccess(response);
      },
      modal: {
        ondismiss: () => {
          if (callbacks.onDismiss) {
            callbacks.onDismiss();
          }
        },
      },
    };

    const razorpay = new window.Razorpay(razorpayOptions);
    razorpay.open();
  } catch (error) {
    console.error('Razorpay initialization error:', error);
    callbacks.onError(error instanceof Error ? error.message : 'Failed to initialize payment');
  }
}

/**
 * Note: Payment signature verification is handled on the backend
 * for security reasons. Do not verify signatures on the client side.
 */

